import { useEffect, useRef } from 'react';

import { useAppDispatch } from '@/shared/hooks';
import { debug } from '@/shared/lib';

import { notificationModel } from '@/entities/notification';

import {
  completeRecording,
  updateUploadProgress,
} from '../model/transcriptorSlice';
import { pollConsultationStatus } from '../model/transcriptorSlice.thunks';

type UseConsultationPollingOptions = {
  consultationId: string | undefined;
  isProcessing: boolean;
  onComplete?: (consultationId: string) => void;
};

export const useConsultationPolling = ({
  consultationId,
  isProcessing,
  onComplete,
}: UseConsultationPollingOptions) => {
  const dispatch = useAppDispatch();
  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollCountRef = useRef(0);
  const baseDelayRef = useRef(2000); // Start with 2 seconds
  const onCompleteRef = useRef(onComplete);
  const isActiveRef = useRef(false);

  // Keep onComplete ref in sync without triggering effect re-runs
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (!consultationId || !isProcessing) {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
      pollCountRef.current = 0;
      isActiveRef.current = false;
      return;
    }

    isActiveRef.current = true;

    // Returns true if polling should stop (terminal state reached)
    const pollStatus = async (): Promise<boolean> => {
      try {
        const result = await dispatch(
          pollConsultationStatus(consultationId),
        ).unwrap();

        dispatch(updateUploadProgress(result.processingProgress));

        if (result.status === 'succeeded') {
          dispatch(completeRecording());
          onCompleteRef.current?.(consultationId);
          return true;
        }

        if (result.status === 'failed') {
          debug('Consultation processing failed:', consultationId);

          dispatch(
            notificationModel.actions.addNotification({
              id: Date.now().toString(),
              type: 'error',
              message: 'Consultation processing failed. Please try again.',
            }),
          );

          return true;
        }

        // Reset error backoff counter on successful non-terminal response
        pollCountRef.current = 0;

        return false;
      } catch (error) {
        debug('Error polling consultation status', error);

        dispatch(
          notificationModel.actions.addNotification({
            id: Date.now().toString(),
            type: 'warning',
            message:
              'Having trouble checking consultation status. Will keep trying...',
          }),
        );

        // Increment counter on error so backoff applies to failures
        pollCountRef.current++;

        return false;
      }
    };

    const scheduleNextPoll = () => {
      if (!isActiveRef.current) {
        return;
      }

      // Calculate delay with exponential backoff on errors
      // Normal: 2s, Error backoff: 4s, 8s, 16s, maxing out at 30s
      const delay = Math.min(
        baseDelayRef.current * Math.pow(2, pollCountRef.current),
        30000,
      );

      debug(
        `Scheduling next poll in ${delay / 1000}s (poll count: ${pollCountRef.current})`,
      );

      pollTimeoutRef.current = setTimeout(async () => {
        const shouldStop = await pollStatus();

        if (!shouldStop && isActiveRef.current) {
          scheduleNextPoll();
        }
      }, delay);
    };

    // Initial poll, then schedule subsequent ones based on result
    pollStatus().then((shouldStop) => {
      if (!shouldStop && isActiveRef.current) {
        scheduleNextPoll();
      }
    });

    return () => {
      isActiveRef.current = false;

      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
    };
  }, [consultationId, isProcessing]);
};
